import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties, ReactNode } from 'react'

/**
 * Reusable typeahead / autocomplete select. Filters options as you type
 * instead of listing everything at once — the same interaction the org-chart
 * approver picker uses, generalised so every "pick one from a long list"
 * field reads consistently.
 *
 * Controlled: the parent owns `value` (`''` = nothing selected). An optional
 * `emptyOptionLabel` renders a clear/none entry at the top of the list.
 * Keyboard: ↑/↓ move, Enter selects, Esc closes the list (stopPropagation so
 * it doesn't also close a host modal).
 */
export interface ComboboxOption {
  value: string
  label: string
  sublabel?: string
  /** Optional right-aligned adornment on the label line — e.g. a type chip. */
  badge?: ReactNode
}

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  fontSize: 14,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
}
// Fixed + portaled to <body> so the list overlays past a host modal's edge
// instead of being clipped by its overflow. Coordinates come from the input's
// bounding rect.
const listStyle: CSSProperties = {
  position: 'fixed',
  zIndex: 1000,
  margin: 0,
  padding: 4,
  listStyle: 'none',
  maxHeight: 220,
  overflowY: 'auto',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  boxShadow: '0 6px 20px rgba(15, 23, 42, 0.2)',
}
const optStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  padding: '6px 8px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  color: 'var(--text)',
}
const subStyle: CSSProperties = { fontSize: 11, color: 'var(--text-3)' }

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Type to search…',
  emptyOptionLabel,
  disabled = false,
  inputTestId,
  optionTestIdPrefix,
  ariaLabel,
  style,
}: {
  options: ReadonlyArray<ComboboxOption>
  value: string
  onChange: (value: string) => void
  placeholder?: string
  emptyOptionLabel?: string
  disabled?: boolean
  inputTestId?: string
  optionTestIdPrefix?: string
  ariaLabel?: string
  style?: CSSProperties
}): JSX.Element {
  const selected = options.find((o) => o.value === value)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const anchorRef = useRef<HTMLInputElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)

  function updateRect(): void {
    if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect())
  }

  // While open, keep the portaled list glued to the input as the page/modal
  // scrolls or resizes.
  useEffect(() => {
    if (!open) return
    updateRect()
    const onMove = (): void => updateRect()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  const q = query.trim().toLowerCase()
  const filtered =
    open && q.length > 0
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            (o.sublabel?.toLowerCase().includes(q) ?? false),
        )
      : options
  const items: ComboboxOption[] = []
  if (emptyOptionLabel != null) items.push({ value: '', label: emptyOptionLabel })
  items.push(...filtered.slice(0, 100))

  function choose(opt: ComboboxOption): void {
    onChange(opt.value)
    setQuery('')
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => Math.min(a + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      const pick = items[active]
      if (open && pick) {
        e.preventDefault()
        choose(pick)
      }
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        ref={anchorRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        aria-label={ariaLabel}
        data-testid={inputTestId}
        disabled={disabled}
        placeholder={placeholder}
        value={open ? query : (selected?.label ?? '')}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          setActive(0)
        }}
        onFocus={() => {
          setQuery('')
          setOpen(true)
          setActive(0)
          updateRect()
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        style={inputStyle}
      />
      {open && rect && items.length > 0
        ? createPortal(
            <ul
              role="listbox"
              style={{
                ...listStyle,
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
              }}
            >
              {items.map((opt, i) => (
                <li
                  key={opt.value || '__empty'}
                  role="option"
                  aria-selected={opt.value === value}
                  data-testid={
                    optionTestIdPrefix
                      ? `${optionTestIdPrefix}${opt.value || 'none'}`
                      : undefined
                  }
                  onMouseDown={(e) => {
                    e.preventDefault() // keep focus so the click registers as a pick
                    choose(opt)
                  }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    ...optStyle,
                    background:
                      i === active
                        ? 'color-mix(in srgb, #3b82f6 14%, transparent)'
                        : 'transparent',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      justifyContent: opt.badge ? 'space-between' : undefined,
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {opt.label}
                    </span>
                    {opt.badge ? (
                      <span style={{ flexShrink: 0 }}>{opt.badge}</span>
                    ) : null}
                  </span>
                  {opt.sublabel ? (
                    <span style={subStyle}>{opt.sublabel}</span>
                  ) : null}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}
